/**
 * Portable, framework-free business-id validation.
 *
 * This file MUST NOT import anything (no Angular, no RxJS, no third-party
 * code): it is copied verbatim into the future Svelte app. Angular form
 * integration lives in the sibling `angular-adapter.ts`, which is the only
 * framework-coupled file.
 *
 * Shape dispatch (the canonical FI and EE forms are disjoint, so the value
 * itself selects the scheme — no country input):
 *
 *   FI (Y-tunnus)       ^\d{7}-\d$      weighted mod 11 (weights 7 9 10 5 8 4 2)
 *   EE (registry code)  ^[1789]\d{7}$   two-pass weighted mod 11, result mod 10
 *
 * Everything else that clears the ASCII charset allowlist is FOREIGN; the
 * policy wrappers (`validateClientBusinessId` /
 * `validateEndCustomerBusinessId`) decide whether FOREIGN is acceptable.
 */

export type BusinessIdScheme = 'FI' | 'EE' | 'FOREIGN';

export type BusinessIdFailureReason =
  | 'empty'
  | 'charset'
  | 'fi-checksum'
  | 'ee-checksum'
  | 'not-fi-ee';

export interface BusinessIdValidationResult {
  valid: boolean;
  scheme: BusinessIdScheme | null;
  /** The value to submit to the backend when valid; null when invalid. */
  canonical: string | null;
  reason: BusinessIdFailureReason | null;
}

const CHARSET = /^[0-9A-Za-z-]+$/;
const FI_DASHED = /^\d{7}-\d$/;
const EE_SHAPE = /^[1789]\d{7}$/;
const EIGHT_DIGITS = /^\d{8}$/;

/**
 * Remove Unicode format (Cf) code points anywhere (e.g. U+200B ZERO WIDTH
 * SPACE, which is invisible to trim-based tooling), then trim leading and
 * trailing Unicode whitespace (JS `String.prototype.trim` strips NBSP too).
 */
export function canonicalizeBusinessId(value: string): string {
  return value.replace(/\p{Cf}/gu, '').trim();
}

/**
 * Finnish Y-tunnus checksum over the seven base digits. Returns the expected
 * check digit, or -1 when the weighted remainder is 1 (which would demand a
 * check digit of 10 — an impossible single digit, so the id is invalid).
 * Port of `cctool/CoronaTool/Utils/FinnishBusinessId.cs`.
 */
function finnishCheckDigit(base: string): number {
  const weights = [7, 9, 10, 5, 8, 4, 2];
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    sum += weights[i] * (base.charCodeAt(i) - 48);
  }
  const check = (11 - (sum % 11)) % 11;
  return check === 10 ? -1 : check;
}

/**
 * Estonian registry-code checksum over the seven base digits. Two-pass:
 * weights 1..7 mod 11; if that yields 10, recompute with weights 3..9 mod 11;
 * then mod 10. Port of `cctool/CoronaTool/Utils/EstonianBusinessId.cs`.
 */
function estonianCheckDigit(base: string): number {
  const digitAt = (i: number): number => base.charCodeAt(i) - 48;

  let checksum = 0;
  for (let i = 0; i < 7; i++) {
    checksum += ((i % 9) + 1) * digitAt(i);
  }
  checksum %= 11;

  if (checksum === 10) {
    checksum = 0;
    for (let i = 0; i < 7; i++) {
      checksum += (((i + 2) % 9) + 1) * digitAt(i);
    }
    checksum %= 11;
  }

  return checksum % 10;
}

function fail(reason: BusinessIdFailureReason): BusinessIdValidationResult {
  return { valid: false, scheme: null, canonical: null, reason };
}

/**
 * Classify and validate a business id. Never performs a network request.
 * FOREIGN results are `valid: true`; policy wrappers narrow that.
 */
export function validateBusinessId(value: string): BusinessIdValidationResult {
  const canonical = canonicalizeBusinessId(value ?? '');

  if (canonical.length === 0) {
    return fail('empty');
  }
  if (!CHARSET.test(canonical)) {
    return fail('charset');
  }

  if (FI_DASHED.test(canonical)) {
    const base = canonical.slice(0, 7);
    const check = Number(canonical.slice(8));
    return finnishCheckDigit(base) === check
      ? { valid: true, scheme: 'FI', canonical, reason: null }
      : fail('fi-checksum');
  }

  if (EE_SHAPE.test(canonical)) {
    const base = canonical.slice(0, 7);
    const check = Number(canonical.slice(7));
    return estonianCheckDigit(base) === check
      ? { valid: true, scheme: 'EE', canonical, reason: null }
      : fail('ee-checksum');
  }

  // Unambiguous dash-less FI: exactly 8 digits whose first digit is 0 or 2–6
  // cannot be an EE code (EE starts 1/7/8/9). Auto-dash only when the FI
  // checksum passes; otherwise fall through to the foreign lane.
  if (EIGHT_DIGITS.test(canonical) && /^[02-6]/.test(canonical)) {
    const base = canonical.slice(0, 7);
    const check = Number(canonical.slice(7));
    if (finnishCheckDigit(base) === check) {
      return { valid: true, scheme: 'FI', canonical: `${base}-${check}`, reason: null };
    }
  }

  return { valid: true, scheme: 'FOREIGN', canonical, reason: null };
}

/**
 * Strict client policy: valid only when the scheme is FI or EE. A FOREIGN
 * value is rejected with reason `not-fi-ee`.
 */
export function validateClientBusinessId(value: string): BusinessIdValidationResult {
  const result = validateBusinessId(value);
  if (result.valid && result.scheme === 'FOREIGN') {
    return { valid: false, scheme: 'FOREIGN', canonical: result.canonical, reason: 'not-fi-ee' };
  }
  return result;
}

/**
 * End-customer policy: FI/EE-shaped values must pass their checksum; anything
 * else that clears the charset allowlist passes on the foreign lane.
 */
export function validateEndCustomerBusinessId(value: string): BusinessIdValidationResult {
  return validateBusinessId(value);
}
